import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { Button, Input, PageHeaderSticky, StickyFooter, toast } from '@/components/ui';
import {
  normalizeTiers,
  QUANTITY_DISCOUNT_TIER_COUNT,
  QUANTITY_DISCOUNT_TYPES,
  QUANTITY_DISCOUNT_VISIBILITIES,
  type QuantityDiscountConfig,
  type QuantityDiscountType,
  type QuantityDiscountVisibility,
} from '../types';
import {
  useQuantityDiscountQuery,
  useSaveQuantityDiscountMutation,
} from '../useQuantityDiscount';

const VISIBILITY_LABELS: Record<QuantityDiscountVisibility, string> = {
  HIDDEN: '미노출',
  B2B: 'B2B 회원에게 노출',
  ALL: '모두에게 노출',
};

const TYPE_LABELS: Record<QuantityDiscountType, string> = {
  AMOUNT: '원',
  PERCENT: '%',
};

/** 그리드 1행 = {수량, 할인}. 빈값은 ''(미입력). 0/빈값 구간은 저장 시 제외. */
interface TierRow {
  minQty: string;
  discount: string;
}

const emptyRows = (): TierRow[] =>
  Array.from({ length: QUANTITY_DISCOUNT_TIER_COUNT }, () => ({ minQty: '', discount: '' }));

/** BE config → 그리드 행. tiers 를 앞에서부터 채우고 나머지는 빈칸. */
const configToRows = (config: QuantityDiscountConfig | null): TierRow[] => {
  const rows = emptyRows();
  if (!config) return rows;
  config.tiers.slice(0, QUANTITY_DISCOUNT_TIER_COUNT).forEach((tier, i) => {
    rows[i] = { minQty: String(tier.minQty), discount: String(tier.discountValue) };
  });
  return rows;
};

const RadioRow = <T extends string>({
  label,
  options,
  labels,
  value,
  onChange,
  name,
}: {
  label: string;
  options: ReadonlyArray<T>;
  labels: Record<T, string>;
  value: T;
  onChange: (next: T) => void;
  name: string;
}) => (
  <div className="grid grid-cols-[120px_1fr] items-center gap-4 py-3" style={{ borderBottom: '1px solid var(--divider-soft)' }}>
    <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
      {label}
    </span>
    <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-5">
      {options.map((opt) => (
        <label key={opt} className="inline-flex cursor-pointer items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
          <input
            type="radio"
            name={name}
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          {labels[opt]}
        </label>
      ))}
    </div>
  </div>
);

export const QuantityDiscountPage = () => {
  const { t } = useTranslation('products');
  const navigate = useNavigate();

  const query = useQuantityDiscountQuery();
  const save = useSaveQuantityDiscountMutation();

  const [visibility, setVisibility] = useState<QuantityDiscountVisibility>('HIDDEN');
  const [discountType, setDiscountType] = useState<QuantityDiscountType>('PERCENT');
  const [rows, setRows] = useState<TierRow[]>(emptyRows);

  // 서버 값으로 1회 시드 (로드 완료 후).
  useEffect(() => {
    if (query.isSuccess) {
      const config = query.data;
      setVisibility(config?.visibility ?? 'HIDDEN');
      setDiscountType(config?.discountType ?? 'PERCENT');
      setRows(configToRows(config ?? null));
    }
  }, [query.isSuccess, query.data]);

  const updateRow = (idx: number, key: keyof TierRow, raw: string) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: raw } : row)));
  };

  const discountUnit = TYPE_LABELS[discountType];

  // 저장 페이로드: 수량 0/빈값 구간 제외 + minQty 오름차순 (normalizeTiers).
  const buildConfig = (): QuantityDiscountConfig => ({
    visibility,
    discountType,
    tiers: normalizeTiers(rows),
  });

  const handleSave = () => {
    if (save.isPending) return;
    save.mutate(buildConfig(), {
      onSuccess: () => toast.success(t('products.quantityDiscount.saved', '수량별 할인 설정을 저장했습니다.')),
      onError: (error) =>
        toast.error(friendlyMessage(error, t('products.quantityDiscount.error', '저장에 실패했습니다.'))),
    });
  };

  // ID49_1.png 브레드크럼: 기본설정 > 상품 정책 > 상품 수량 별 할인 설정 (전역 설정).
  const breadcrumb = useMemo(
    () => [
      { label: t('products.quantityDiscount.breadcrumbRoot', '기본 설정') },
      { label: t('products.quantityDiscount.breadcrumbGroup', '상품 정책') },
      { label: t('products.quantityDiscount.breadcrumb', '상품 수량별 할인 설정') },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-6 pb-20">
      <PageHeaderSticky
        title={t('products.quantityDiscount.title', '상품 수량 별 할인 설정')}
        breadcrumb={breadcrumb}
      />

      <p className="flex flex-col gap-1 text-[13px]" style={{ color: 'var(--content-muted)' }}>
        <span>{t('products.quantityDiscount.hint', '상품 수량을 0개로 설정한 부분은 할인이 적용되지 않습니다.')}</span>
        <span>{t('products.quantityDiscount.perProductNote', '수량은 옵션(variant)별이 아니라 동일 상품의 합산 구매 수량 기준으로 구간이 적용됩니다.')}</span>
      </p>

      {query.isPending ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl py-12 text-center text-sm"
          style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)', color: 'var(--content-muted)' }}
        >
          {t('products.quantityDiscount.loading', '불러오는 중…')}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-xl py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('products.quantityDiscount.loadFailed', '설정을 불러오지 못했습니다.')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('products.actions.retry', '다시 시도')}
          </Button>
        </div>
      ) : (
        <section
          className="rounded-lg p-6"
          style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
        >
          <h3 className="mb-2 text-[15px] font-bold" style={{ color: 'var(--content-strong)' }}>
            {t('products.quantityDiscount.basic', '기본 설정')}
          </h3>

          <RadioRow
            name="qd-visibility"
            label={t('products.quantityDiscount.visibility', '노출 여부')}
            options={QUANTITY_DISCOUNT_VISIBILITIES}
            labels={VISIBILITY_LABELS}
            value={visibility}
            onChange={setVisibility}
          />
          <RadioRow
            name="qd-type"
            label={t('products.quantityDiscount.discountType', '할인 타입')}
            options={QUANTITY_DISCOUNT_TYPES}
            labels={TYPE_LABELS}
            value={discountType}
            onChange={setDiscountType}
          />

          {/* 수량 행 */}
          <div className="grid grid-cols-[120px_1fr] items-start gap-4 py-3" style={{ borderBottom: '1px solid var(--divider-soft)' }}>
            <span className="pt-2 text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
              {t('products.quantityDiscount.qty', '수량')}
            </span>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${QUANTITY_DISCOUNT_TIER_COUNT}, minmax(0, 1fr))` }}>
              {rows.map((row, idx) => (
                <div key={`qty-${idx}`} className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={0}
                    step="1"
                    inputMode="numeric"
                    value={row.minQty}
                    onChange={(e) => updateRow(idx, 'minQty', e.target.value)}
                    aria-label={t('products.quantityDiscount.qtyAria', { index: idx + 1, defaultValue: `구간 ${idx + 1} 수량` })}
                    className="w-full"
                  />
                  <span className="shrink-0 text-[12px]" style={{ color: 'var(--content-muted)' }}>{t('products.quantityDiscount.qtyUnit', '개')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 할인 행 */}
          <div className="grid grid-cols-[120px_1fr] items-start gap-4 py-3">
            <span className="pt-2 text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
              {t('products.quantityDiscount.discount', '할인')}
            </span>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${QUANTITY_DISCOUNT_TIER_COUNT}, minmax(0, 1fr))` }}>
              {rows.map((row, idx) => (
                <div key={`disc-${idx}`} className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={0}
                    step="1"
                    inputMode="numeric"
                    value={row.discount}
                    onChange={(e) => updateRow(idx, 'discount', e.target.value)}
                    aria-label={t('products.quantityDiscount.discountAria', { index: idx + 1, defaultValue: `구간 ${idx + 1} 할인` })}
                    className="w-full"
                  />
                  <span className="shrink-0 text-[12px]" style={{ color: 'var(--content-muted)' }}>{discountUnit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <StickyFooter>
        <Button type="button" variant="outline" onClick={() => navigate('/products')} disabled={save.isPending}>
          {t('products.actions.cancel', '취소')}
        </Button>
        <Button type="button" variant="cta" onClick={handleSave} disabled={save.isPending || query.isPending}>
          {save.isPending ? t('products.quantityDiscount.saving', '저장 중…') : t('products.quantityDiscount.save', '저장')}
        </Button>
      </StickyFooter>
    </div>
  );
};
